/* eslint-disable camelcase */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getIsActiveTypesFilter } from '@features/TasksExtraFilters/lib/helpers/getIsActiveTypesFilter';
import { useYandexMetrika } from '@shared/lib';
import { getEventNameByFilter } from '@shared/lib/yandexMetrica/lib/getEventNameByFilter';
import { ChangeTaskFilter, TaskBoardViewState, taskViewStateApi, TasksTypes } from 'entities/task';
import { fetchGanttTasks } from 'entities/task/api/fetchGanttTasks';
import { tasksInformationActions } from 'entities/task/model/slice/tasksInformation';
import { tasksSelector } from 'entities/task/model/slice/taskSlice';
import { useAppDispatch, useAppSelector } from 'store';
import { taskBoardActions } from 'store/slices/taskBoardSlice/taskBoardSlice';

import { filterBoard } from '../helpers/filterBoard';
import { filterList } from '../helpers/filterListI';
import { filterTasks } from '../helpers/filterTasks';
import { Filters } from '../types/taskHeader';

export interface TaskHeaderOptions {
    initialViewState: TaskBoardViewState;
    tasksLoaded: boolean;
    onFiltersApplied: () => void;
}

export const useTaskHeader = ({
    initialViewState,
    tasksLoaded,
    onFiltersApplied,
}: TaskHeaderOptions) => {
    const dispatch = useAppDispatch();
    const { tags, tabs, isSorted, isShowCompleted, isFiltered, isNeedFetchGant } = useAppSelector(
        (state) => state.tasksInformationReducer
    );
    const { isLoading } = useAppSelector((state) => state.tasksReducer);
    const tasks = useAppSelector(tasksSelector.selectAll);
    const [initialState] = useState(initialViewState);
    const [filteredData, setFilteredData] = useState<Filters>(initialState.filters);
    const [input, setInput] = useState(initialState.filters.search);
    const [isHydrated, setIsHydrated] = useState(false);
    const appliedFiltersKey = useRef<string>();
    const lastSubmittedState = useRef<string>();
    const { trackEvent } = useYandexMetrika();
    const queryClient = useQueryClient();
    const { queryKey } = taskViewStateApi.queryOptions();
    const {
        mutate,
        isError: isSaveError,
        isPending: isSaving,
    } = useMutation({
        mutationFn: taskViewStateApi.update,
        scope: { id: queryKey.join(':') },
        onMutate: async (nextState) => {
            await queryClient.cancelQueries({ queryKey });
            const previousState = queryClient.getQueryData<TaskBoardViewState>(queryKey);
            const optimisticState: TaskBoardViewState = previousState
                ? {
                      ...previousState,
                      ...nextState,
                      filters: { ...previousState.filters, ...nextState.filters },
                  }
                : (nextState as TaskBoardViewState);
            queryClient.setQueryData(queryKey, optimisticState);

            return { previousState };
        },
        onError: (_error, _nextState, context) => {
            if (context?.previousState) {
                queryClient.setQueryData(queryKey, context.previousState);
            }
        },
        onSuccess: (data) => {
            queryClient.setQueryData(queryKey, data);
        },
    });
    const mutateRef = useRef(mutate);
    mutateRef.current = mutate;
    const currentState = useMemo<TaskBoardViewState>(
        () => ({
            filters: filteredData,
            is_sorted: isSorted,
            is_show_completed: isShowCompleted,
        }),
        [filteredData, isSorted, isShowCompleted]
    );
    const persistedState = useMemo(() => {
        const persistedFilters = { ...currentState.filters };
        delete persistedFilters.search;

        return {
            filters: persistedFilters,
            is_sorted: currentState.is_sorted,
            is_show_completed: currentState.is_show_completed,
        };
    }, [currentState]);
    const serializedPersistedState = JSON.stringify(persistedState);

    useLayoutEffect(() => {
        dispatch(tasksInformationActions.changeSort(initialState.is_sorted));
        dispatch(tasksInformationActions.changeShowCompleted(initialState.is_show_completed));
        const initialPersistedFilters = { ...initialState.filters };
        delete initialPersistedFilters.search;
        lastSubmittedState.current = JSON.stringify({
            filters: initialPersistedFilters,
            is_sorted: initialState.is_sorted,
            is_show_completed: initialState.is_show_completed,
        });
        setIsHydrated(true);
    }, [dispatch, initialState]);

    useEffect(() => {
        if (!isHydrated || serializedPersistedState === lastSubmittedState.current) return;
        lastSubmittedState.current = serializedPersistedState;
        mutateRef.current(persistedState);
    }, [isHydrated, persistedState, serializedPersistedState]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setFilteredData((previous) =>
                previous.search === input ? previous : { ...previous, search: input }
            );
        }, 500);
        return () => clearTimeout(timeout);
    }, [input]);

    const isActiveTypesFilter = getIsActiveTypesFilter(filteredData.type);
    useLayoutEffect(() => {
        if (!tasksLoaded || isLoading || !isHydrated) return;
        const nextFiltersKey = JSON.stringify({ tabs, filteredData });
        if (appliedFiltersKey.current === nextFiltersKey) return;
        appliedFiltersKey.current = nextFiltersKey;
        const filteredTasksId = filterTasks(tasks, filteredData);
        dispatch(
            tasksInformationActions.changeFilteredBoard({
                board: filterBoard(tabs.board, filteredTasksId),
                list: filterList(tabs.list, filteredTasksId),
            })
        );
        dispatch(
            tasksInformationActions.setIsFiltered(
                !!filteredData.search ||
                    !!filteredData.colors.length ||
                    !!filteredData.executors.length ||
                    !!filteredData.tags.length ||
                    !!filteredData.projects ||
                    !!filteredData.estimate ||
                    isActiveTypesFilter
            )
        );
        onFiltersApplied();
    }, [
        dispatch,
        tasksLoaded,
        isLoading,
        isHydrated,
        tasks,
        tabs,
        filteredData,
        isActiveTypesFilter,
        onFiltersApplied,
    ]);

    const onClickOpenCreateTask = useCallback(() => {
        dispatch(taskBoardActions.onModalMode('addNewTask'));
    }, [dispatch]);
    const onFilteredData: ChangeTaskFilter = (name, value) => {
        const eventName = getEventNameByFilter(name);
        trackEvent('tasks', eventName, {
            filterValue: value,
            actionData: `отфильтровали по ${eventName}`,
        });
        setFilteredData((previous) => ({
            ...previous,
            ...(name === 'projects' ? { estimate: null } : {}),
            ...(name === 'estimate' ? { projects: null } : {}),
            [name]: value,
        }));
    };
    const isGanttActive = filteredData.type.includes(TasksTypes.GANTT);
    const isGanttDisabled = filteredData.type.length === 1 && isGanttActive;
    const onToggleGanttTasks = () => {
        if (isGanttDisabled) return;
        if (isNeedFetchGant) {
            dispatch(fetchGanttTasks());
        }

        if (isGanttActive) {
            dispatch(tasksInformationActions.setShowGanttTabs(false));
            onFilteredData(
                'type',
                filteredData.type.filter((item) => item !== TasksTypes.GANTT)
            );
            return;
        }

        dispatch(tasksInformationActions.setShowGanttTabs(true));
        onFilteredData('type', [...filteredData.type, TasksTypes.GANTT]);
    };
    const clearFilters = () => {
        setFilteredData({
            search: '',
            type: [TasksTypes.CUSTOM, TasksTypes.PAYMENT, TasksTypes.SUPPLY_REQUEST],
            estimate: null,
            projects: null,
            tags: [],
            colors: [],
            executors: [],
        });
        setInput('');
        dispatch(tasksInformationActions.setShowGanttTabs(false));
        dispatch(tasksInformationActions.changeSort(false));
    };
    const tagsList = tags.map((tag) => ({ value: tag.id, title: tag.title }));
    const selectedTags = tagsList.filter((tag) => filteredData.tags.includes(tag.value));
    const changeSelectedTags = (value: string) =>
        onFilteredData('tags', [...filteredData.tags, +value]);
    const deleteTag = (value: string) =>
        onFilteredData(
            'tags',
            filteredData.tags.filter((id) => id !== +value)
        );
    const resetTags = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        onFilteredData('tags', []);
    };

    const onChange = (_name: string, value: string) => setInput(value);
    const retrySave = () => mutate(currentState);
    return {
        onClickOpenCreateTask,
        selectedTags,
        tagsList,
        deleteTag,
        changeSelectedTags,
        input,
        onChange,
        onFilteredData,
        filteredData,
        clearFilters,
        isFiltered,
        resetTags,
        isSaveError,
        isSaving,
        retrySave,
        isGanttActive,
        isGanttDisabled,
        onToggleGanttTasks,
    };
};
